import _get from 'lodash/get'
import * as z from 'zod'

import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import type { StepConfigParams } from '@flows/configs/types'
import type { AnyStepConfiguration } from '@flows/core/types/stepTypes'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'

import dental_previous_insurance, { claimSublimitRows } from './dental_previous_insurance'
import {
  eligibilitySchemaShape,
  getIneligibleStep,
  healthInsuranceTypeChoice,
  infoTooltip,
  missingTeethChoice,
  periodontalDiseaseChoice,
  previousInsuranceOptions,
} from './questions'

const dental_coverage = (params: StepConfigParams) => {
  const getParamPath = params.helperFunctions.getParamPath

  return {
    schema: schemaUtils.generateZodObjectFromPaths({
      ...eligibilitySchemaShape(getParamPath),
      [getParamPath('previous_insurance')]: z.enum(['unknown', 'true', 'false']),
    }),
    onPrepare: dental_previous_insurance(params, 'web').onPrepare,
    showOrderSummary: true,
    buttonLabel: {
      type: 'translatableText',
      translationKey: 'sharedFlows_dental:steps.dental_coverage.submitButton',
    },
    stepContent: [
      {
        type: 'harmonyTitle',
        fontSize: 'primary',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_dental:steps.dental_coverage.title',
        },
      },
      {
        type: 'title',
        isQuestion: true,
        spaceBefore: 'largeMedium',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_dental:steps.dental_coverage.health_insurance.title',
        },
      },
      healthInsuranceTypeChoice(getParamPath),
      {
        type: 'title',
        isQuestion: true,
        spaceBefore: 'largeMedium',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_dental:steps.dental_coverage.missing_teeth.title',
        },
        infoButton: infoTooltip('sharedFlows_dental:steps.dental_instant.missing_teeth.info.text'),
      },
      missingTeethChoice(getParamPath),
      {
        type: 'title',
        isQuestion: true,
        spaceBefore: 'largeMedium',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_dental:steps.dental_coverage.periodontal_disease.title',
        },
      },
      periodontalDiseaseChoice(getParamPath),
      {
        type: 'title',
        isQuestion: true,
        spaceBefore: 'largeMedium',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_dental:steps.dental_previous_insurance.title',
        },
      },
      {
        type: 'choice',
        variant: 'filled',
        shouldTrackValues: true,
        shouldAutoSubmit: false,
        name: getParamPath('previous_insurance'),
        options: previousInsuranceOptions,
      },
      {
        type: 'infoBox',
        bgColor: 'grey100',
        spaceBefore: 'small',
        contentConfiguration: [
          {
            type: 'title',
            isQuestion: true,
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_dental:steps.dental_previous_insurance.info.title',
            },
          },
          {
            type: 'text',
            style: 'caption',
            color: 'black',
            weight: 'regular',
            emphasis: 'default',
            spaceBefore: 'mini',
            text: {
              type: 'translatableText',
              translationKey:
                'sharedFlows_dental:steps.dental_coverage.previous_insurance_info.text',
            },
          },
          {
            type: 'text',
            style: 'caption',
            color: 'black',
            weight: 'semibold',
            emphasis: 'default',
            spaceBefore: 'xsmall',
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_dental:steps.dental_previous_insurance.info.limitsTitle',
            },
          },
          {
            type: 'list',
            padding: 'none',
            paddingLeft: 'none',
            spaceBefore: 'small',
            data: claimSublimitRows(params),
          },
        ],
      },
      {
        type: 'infoBox',
        bgColor: 'grey100',
        spaceBefore: 'largeMedium',
        contentConfiguration: [
          {
            type: 'title',
            isQuestion: true,
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_dental:steps.general_notice.title',
            },
          },
          {
            type: 'text',
            style: 'caption',
            color: 'black',
            weight: 'regular',
            emphasis: 'default',
            spaceBefore: 'mini',
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_dental:steps.general_notice.description',
            },
          },
        ],
      },
    ],
    subFlow: SubFlow.Common,
    nextStepConfig: {
      valueFunction: (flowValues) => {
        const ineligibleStep = getIneligibleStep(flowValues, getParamPath)
        if (ineligibleStep) {
          return ineligibleStep
        }
        return _get(flowValues, getParamPath('previous_insurance')) === 'true'
          ? 'dental_previous_insurer'
          : 'payment'
      },
      valueToStepName: {
        failure: 'failure',
        dental_already_covered: 'dental_already_covered',
        dental_previous_insurer: 'dental_previous_insurer',
        payment: 'payment',
      },
    },
    onSubmit: dental_previous_insurance(params, 'web').onSubmit,
  } as const satisfies AnyStepConfiguration
}

export default dental_coverage
